import { _ } from "../polyglot/polyglot.js";
import { eventManager } from "@foretagsplatsen/yaem";
import { groupItems } from "../elements/select/migration.js";
import { select, theme } from "../elements/index.js";
import { widget } from "@foretagsplatsen/widgetjs";

/**
 * Displays selectable periods (months/years/halfyears/quarters) and keeps track on which
 * period is selected.
 *
 * _Usage:_
 *
 *    var periodPicker = periodPickerWidget(options);
 *
 * _Options:_
 *
 * showSubPeriod			Bool. Show Month/Quarter/HalfYear selector if true. (default false)
 * syncWithGlobalPeriod	Bool. Listens for global period changes and updates widget if true (default true)
 * redirectOnChange		Bool. Automatically redirects to selected period if true (default true).
 * selectedPeriod			Period to display as default (default period in session)
 */
export default function periodPickerWidget(spec, my) {
	my ||= {};
	spec ||= {};

	// Options
	const showSubPeriod = spec.showSubPeriod !== false;
	const syncWithGlobalPeriod = spec.syncWithGlobalPeriod !== false;
	const redirectOnChange = spec.redirectOnChange !== false;

	// Selected period is period given in options or
	// current period in session
	let selectedPeriod = spec.period || spec.selectedPeriod;
	const activeYears = spec.activeYears;
	const redirectToPeriod = spec.redirectToPeriod;

	const that = widget(spec, my);

	/*
	 * WIDGETS
	 */

	my.years = makeYearItems(activeYears.slice(0).reverse());
	my.subPeriods = makeSubPeriodItems();

	let yearPicker = null;
	let subPeriodPicker = null;

	that.widgets = function () {
		return [yearPicker, subPeriodPicker];
	};

	/*
	 * Events
	 */

	// Redirect when period change
	if (redirectOnChange) {
		that.register("periodChanged", redirectToPeriod);
	}

	// Update period when global period change
	if (syncWithGlobalPeriod) {
		eventManager.at("period").register("change", (period) => {
			that.setPeriod(period);
		});
	}

	// API

	that.getPeriod = function () {
		return selectedPeriod;
	};

	that.setYears = function (years) {
		my.years = makeYearItems(years);

		that.update();
	};

	that.setPeriodType = function (periodType) {
		that.setPeriod(selectedPeriod.asPeriodType(periodType));
	};

	that.setPeriod = function (newPeriod) {
		if (!newPeriod || !newPeriod.getYear()) {
			throw new Error("Invalid period");
		}

		if (selectedPeriod !== newPeriod) {
			selectedPeriod = newPeriod;
			my.subPeriods = makeSubPeriodItems();
		}

		my.selectedYear = selectedPeriod.getYear();

		that.update();
	};

	that.updatePeriods = function () {
		my.subPeriods = makeSubPeriodItems();
		that.setYears(activeYears.slice(0).reverse());
	};

	that.onPeriodChange = function (callback) {
		that.on("periodChanged", callback);
	};

	/*
	 * Render
	 */

	that.renderContentOn = function (html) {
		yearPicker = select({
			items: my.years,
			ref: (ref) => ref.setAttribute("name", "year"),
			class: "right-menu",
			selected: selectedPeriod.getYear(),
			onChange: onYearChange,
		});

		subPeriodPicker = select({
			items: my.subPeriods,
			ref: (ref) => ref.setAttribute("name", "month"),
			class: "right-menu",
			selected: selectedPeriod,
			onChange: (newPeriod) => {
				that.setPeriod(newPeriod);
				that.trigger("periodChanged", selectedPeriod);
			},
		});

		html.render(
			theme(
				{ name: "period-picker" },
				html.div(
					{
						class: "period_picker clearfix",
						"data-test": "period-picker",
						"data-help": "period-picker",
					},
					yearPicker,
					(html) => {
						if (!showSubPeriod) {
							return;
						}
						html.render(subPeriodPicker);
					},
				),
			),
		);
	};

	function onYearChange(newYear) {
		// try to find selected period in new year
		let newPeriod;
		const samePeriodsInNewYear =
			newYear.findSameCalendarPeriods(selectedPeriod);
		if (samePeriodsInNewYear.length === 0) {
			// default to current period in selected year
			newPeriod = showSubPeriod
				? newYear.getLastActivePeriod(selectedPeriod.getPeriodType())
				: newYear;
		} else {
			// Just pick the last period even if the fiscal year contains many
			// (this can happen in if the fiscal year is an extended one)
			newPeriod = samePeriodsInNewYear.at(-1);
		}

		that.setPeriod(newPeriod);
		that.trigger("periodChanged", selectedPeriod);
	}

	function getAllPeriods() {
		return selectedPeriod.getYear().getAllActivePeriods();
	}

	function makeYearItems(years) {
		return years.map((year) => ({
			label: year.getName(),
			value: year,
		}));
	}

	function makeSubPeriodItems() {
		const items = getAllPeriods().map((period) => ({
			label: period.isYear() ? _("Year") : period.getName(),
			value: period,
		}));

		return groupItems({
			items,
			getGroup: (period) => period.getGroupLabel(),
			labelForGroup: (group) => group.toString(),
		});
	}

	return that;
}
